import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  makeStyles,
} from '@material-ui/core';
import type { ReactNode } from 'react';
import type { ActivityDeletionBatchView } from '@internal/spassu-code-contract';
import type { AdminDeletionPanelProps } from './AdminDeletionPanel';

const useStyles = makeStyles(theme => ({
  warning: { color: theme.palette.error.main, fontWeight: 600 },
}));

export function DestructiveConfirmDialog(props: {
  open: boolean;
  titleId: string;
  title: string;
  /**
   * Prose and children are additive: the batch dialog passes only children
   * (BatchTargets carries its own prose), while the override passes both — the
   * consequence in words AND the activity it will delete.
   */
  warning?: string;
  body?: string;
  children?: ReactNode;
  confirmLabel: string;
  busy: boolean;
  onClose(): void;
  onConfirm(): void;
}) {
  const classes = useStyles();
  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      aria-labelledby={props.titleId}
    >
      <DialogTitle id={props.titleId}>{props.title}</DialogTitle>
      <DialogContent>
        {props.warning || props.body ? (
          <DialogContentText>
            <span className={classes.warning}>{props.warning}</span>{' '}
            {props.body}
          </DialogContentText>
        ) : null}
        {props.children}
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} disabled={props.busy}>
          Voltar
        </Button>
        <Button
          color="secondary"
          variant="contained"
          disabled={props.busy}
          onClick={props.onConfirm}
        >
          {props.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export const BATCH_CONFIRMATION = {
  titleId: 'confirm-batch-title',
  title: 'Excluir atividades selecionadas?',
  confirmLabel: 'Confirmar exclusão',
};

export const OVERRIDE_CONFIRMATION = {
  titleId: 'confirm-override-title',
  title: 'Ignorar a exclusão local?',
  warning: 'O workspace local permanecerá no dispositivo.',
  body: `Os dados centrais desta atividade serão removidos mesmo sem a
  confirmação do agente. A credencial local é invalidada, e um retorno posterior
  do agente não receberá comando algum.`,
  confirmLabel: 'Confirmar e excluir',
};

/**
 * Names every target explicitly. The previous version showed only a count, so the
 * administrator confirmed an irreversible, tombstone-free deletion without ever
 * seeing WHICH activities it covered — a selection made rows earlier, possibly
 * across filter changes.
 */
export function BatchTargets({
  targets,
}: {
  targets: AdminDeletionPanelProps['selectedTargets'];
}) {
  const classes = useStyles();
  return (
    <>
      <Typography variant="body2" component="p">
        <span className={classes.warning}>
          Esta exclusão é definitiva e não deixa registro.
        </span>{' '}
        Todos os dados centrais — Cockpit, evidências, métricas, artefatos e
        auditoria — serão removidos. Atividades concluídas são protegidas e
        serão recusadas. O workspace local é excluído antes do apagamento
        central.
      </Typography>
      <Typography variant="overline" component="h3">
        {`Serão excluídas (${targets.length})`}
      </Typography>
      <ul aria-label="Atividades selecionadas para exclusão">
        {targets.map(target => (
          <li key={target.id}>
            <Typography variant="body2" component="span">
              {target.repoName
                ? `${target.title} · ${target.repoName}`
                : target.title}
            </Typography>
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * Names the single activity an override will delete. The batch dialog lists its
 * targets, but the per-item override rendered generic copy only — and it is the
 * more dangerous of the two, since it removes central data while knowingly
 * abandoning a local workspace.
 */
export function OverrideTarget({
  activityId,
  targets,
  items,
}: {
  activityId: string;
  targets: AdminDeletionPanelProps['selectedTargets'];
  items: ActivityDeletionBatchView['items'] | undefined;
}) {
  const known = targets.find(target => target.id === activityId);
  const label = known
    ? [known.title, known.repoName].filter(Boolean).join(' · ')
    : items?.find(
        (item: ActivityDeletionBatchView['items'][number]) =>
          item.activityId === activityId,
      )?.activityId;
  return (
    <Typography variant="body2" component="p">
      <strong>{label ?? activityId}</strong>
    </Typography>
  );
}

/** Override confirmation, extracted so the panel stays within its size budget. */
export function OverrideConfirmation({
  activityId,
  targets,
  items,
  busy,
  onClose,
  onConfirm,
}: {
  activityId: string | null;
  targets: AdminDeletionPanelProps['selectedTargets'];
  items: ActivityDeletionBatchView['items'] | undefined;
  busy: boolean;
  onClose(): void;
  onConfirm(activityId: string): void;
}) {
  return (
    <DestructiveConfirmDialog
      {...OVERRIDE_CONFIRMATION}
      open={activityId !== null}
      busy={busy}
      onClose={onClose}
      onConfirm={() => {
        if (activityId) onConfirm(activityId);
      }}
    >
      {activityId !== null ? (
        <OverrideTarget
          activityId={activityId}
          targets={targets}
          items={items}
        />
      ) : null}
    </DestructiveConfirmDialog>
  );
}
